import React, { useState, useEffect, useCallback } from 'react';
import { useMqttProxy } from '../mqttProxy';
import { sendTamAck } from '../tamAck';

const API_BASE = '';

const TAM_CFG = {
  '1': { title:'Pump Tamper',        badge:'PUMP TAMPER',        color:'#FF3B30' },
  '2': { title:'Flow Sensor Tamper', badge:'FLOW SENSOR TAMPER', color:'#FF9F0A' },
  '3': { title:'Higher OutFlow',            badge:'Higher OutFlow',            color:'#0EA5E9' },
  '4': { title:'Refreshments',       badge:'REFRESH – EVERY 2 YEARS', color:'#34C759' },
};

const TAM_KEY = (mac) => `hoags_tam_history_${mac}`;

function getAllTamHistory(devices) {
  const all = [];
  devices.forEach(d => {
    try {
      const mac = (d.mac_id||d.macid||'').replace(/[^a-fA-F0-9]/g,'').toLowerCase();
      const raw = JSON.parse(localStorage.getItem(TAM_KEY(mac)) || '[]');
      // STRICT dedup: keep ONLY 1 entry per tamCode — the most recent one
      // This permanently removes all duplicates on every read
      const latestPerCode = new Map();
      raw.forEach(ev => {
        const existing = latestPerCode.get(ev.tamCode);
        if (!existing || new Date(ev.detectedAt) >= new Date(existing.detectedAt)) {
          latestPerCode.set(ev.tamCode, ev);
        }
      });
      const deduped = Array.from(latestPerCode.values());
      // Always write back — cleans old duplicates permanently
      localStorage.setItem(TAM_KEY(mac), JSON.stringify(deduped));
      deduped.forEach(ev => all.push({
        ...ev, mac,
        deviceName: d.friendly_name||d.friendlyname||mac,
      }));
    } catch {}
  });
  // Also clean any mac not in devices list (orphaned localStorage keys)
  try {
    Object.keys(localStorage)
      .filter(k => k.startsWith('hoags_tam_history_'))
      .forEach(k => {
        const raw = JSON.parse(localStorage.getItem(k) || '[]');
        const latestPerCode = new Map();
        raw.forEach(ev => {
          const existing = latestPerCode.get(ev.tamCode);
          if (!existing || new Date(ev.detectedAt) >= new Date(existing.detectedAt)) {
            latestPerCode.set(ev.tamCode, ev);
          }
        });
        const deduped = Array.from(latestPerCode.values());
        if (deduped.length !== raw.length) {
          localStorage.setItem(k, JSON.stringify(deduped));
        }
      });
  } catch {}
  return all.sort((a,b) => new Date(b.detectedAt) - new Date(a.detectedAt));
}

function acknowledgeTamEvent(mac, id) {
  try {
    const hist = JSON.parse(localStorage.getItem(TAM_KEY(mac)) || '[]');
    const idx  = hist.findIndex(h => h.id === id);
    if (idx >= 0) {
      hist[idx].acknowledgedAt = new Date().toISOString();
      localStorage.setItem(TAM_KEY(mac), JSON.stringify(hist));
    }
  } catch {}
}

const STYLES = `
  @keyframes mFadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
  .m-card { transition:box-shadow .2s,transform .2s; }
  .m-card:hover { box-shadow:0 8px 28px rgba(0,0,0,.1)!important; transform:translateY(-2px); }
`;

export default function Miscellaneous({ subPage }) {
  // subPage: null = show all, 'pump' = tamCode '1', 'flow' = tamCode '2'
  const [devices,  setDevices]  = useState([]);
  const [events,   setEvents]   = useState([]);
  const [filter,   setFilter]   = useState('All');
  const [loading,  setLoading]  = useState(true);
  const { publish } = useMqttProxy();

  const fetchAndLoad = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/api/admin-devices`, { headers:{'ngrok-skip-browser-warning':'true'} });
      const data = await res.json();
      const arr  = Array.isArray(data) ? data : (data.registrations??data.data??data.devices??[]);
      // Dedupe by normalized MAC — the registration API can return the same
      // device multiple times, which previously caused getAllTamHistory to
      // read the same localStorage history repeatedly and push identical
      // entries into the list once per duplicate device record.
      const byMac = new Map();
      arr.forEach(d => {
        const mac = (d.mac_id || d.macid || '').replace(/[^a-fA-F0-9]/g, '').toLowerCase();
        if (mac && !byMac.has(mac)) byMac.set(mac, d);
      });
      const deduped = Array.from(byMac.values());
      setDevices(deduped);
      setEvents(getAllTamHistory(deduped));
    } catch(e) {
      // fallback: try loading from localStorage with empty devices list
      setEvents(getAllTamHistory([]));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAndLoad(); }, [fetchAndLoad]);

  const refresh = () => setEvents(getAllTamHistory(devices));

  const handleAck = (mac, id) => {
    acknowledgeTamEvent(mac, id);
    refresh();
    // Send encrypted TAM ACK to device
    sendTamAck(mac, publish);
  };

  const handleClearAll = () => {
    if (!window.confirm('Clear ALL tamper history across all devices?')) return;
    devices.forEach(d => {
      const mac = (d.mac_id||d.macid||'').replace(/[^a-fA-F0-9]/g,'').toLowerCase();
      localStorage.removeItem(TAM_KEY(mac));
    });
    setEvents([]);
  };

  // Derive active tamCode from subPage
  const subTamCode = subPage === 'pump' ? '1' : subPage === 'flow' ? '2'
    : subPage === 'dryrun' ? '3' : subPage === 'refreshments' ? '4' : null;
  const subLabel   = subPage === 'pump' ? 'Pump Tamper' : subPage === 'flow' ? 'Flow Sensor'
    : subPage === 'dryrun' ? 'Higher OutFlow' : subPage === 'refreshments' ? 'Refreshments' : null;
  const subColor   = subPage === 'pump' ? '#FF3B30' : subPage === 'flow' ? '#FF9F0A'
    : subPage === 'dryrun' ? '#0EA5E9' : subPage === 'refreshments' ? '#34C759' : null;

  const pump        = events.filter(e => e.tamCode === '1').length;
  const sensor       = events.filter(e => e.tamCode === '2').length;
  const dryrun       = events.filter(e => e.tamCode === '3').length;
  const refreshments = events.filter(e => e.tamCode === '4').length;
  const unacked  = events.filter(e => !e.acknowledgedAt).length;
  const affected = [...new Set(events.map(e => e.mac))].length;

  // Apply filter tabs + subPage filter
  let filtered = filter === 'All'            ? events
    : filter === 'Unacknowledged'              ? events.filter(e => !e.acknowledgedAt)
    : filter === 'Pump'                        ? events.filter(e => e.tamCode === '1')
    : filter === 'Flow Sensor'                 ? events.filter(e => e.tamCode === '2')
    : filter === 'Higher OutFlow'              ? events.filter(e => e.tamCode === '3')
    : filter === 'Refreshments'                ? events.filter(e => e.tamCode === '4')
    : events;
  // SubPage overrides tab filter
  if (subTamCode) filtered = events.filter(e => e.tamCode === subTamCode);

  return (
    <div style={{ fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',sans-serif", maxWidth:900 }}>
      <style>{STYLES}</style>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between',
        flexWrap:'wrap', gap:14, marginBottom:28 }}>
        <div>
          <img src="/havells-logo.png" alt="Havells" style={{height:48,objectFit:'contain',marginBottom:10,display:'block'}}/>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <h1 style={{ fontSize:28, fontWeight:800, color:'#1c1c1e', letterSpacing:-.6, margin:0 }}>
              {subLabel ? subLabel : 'Miscellaneous'}
            </h1>
            {subLabel && (
              <span style={{
                fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20,
                background:`${subColor}15`, color:subColor, letterSpacing:.4,
              }}>
                {TAM_CFG[subTamCode]?.badge}
              </span>
            )}
          </div>
          <p style={{ color:'#8E8E93', fontSize:14, marginTop:4 }}>
            {subLabel
              ? `${filtered.length} device${filtered.length!==1?'s':''} with ${subLabel} alerts`
              : 'Tamper alert history across all devices'
            }
          </p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={fetchAndLoad} disabled={loading} style={{
            display:'inline-flex', alignItems:'center', gap:7,
            padding:'8px 16px', borderRadius:12, border:'none',
            background:'linear-gradient(135deg,#007AFF,#0063CC)',
            color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer',
            boxShadow:'0 4px 14px rgba(0,122,255,.3)', opacity:loading?.6:1,
            fontFamily:'inherit',
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
              <path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/>
            </svg>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          {events.length > 0 && (
            <button onClick={handleClearAll} style={{
              padding:'8px 16px', borderRadius:12, border:'1.5px solid rgba(255,59,48,.25)',
              background:'rgba(255,59,48,.06)', color:'#FF3B30',
              fontWeight:600, fontSize:13, cursor:'pointer', fontFamily:'inherit',
            }}>Clear All</button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',
        gap:12, marginBottom:24 }}>
        {[
          { label:'Total Events',     value:events.length, color:'#007AFF' },
          { label:'Pump Tampers',     value:pump,          color:'#FF3B30' },
          { label:'Flow Sensor',      value:sensor,        color:'#FF9F0A' },
          { label:'Higher OutFlow',     value:dryrun,        color:'#0EA5E9' },
          { label:'Refreshments',     value:refreshments,  color:'#34C759' },
          { label:'Unacknowledged',   value:unacked,       color:'#FF3B30' },
          { label:'Devices Affected', value:affected,      color:'#AF52DE' },
        ].map(s => (
          <div key={s.label} className="m-card" style={{
            background:'#fff', borderRadius:16, padding:'16px 18px',
            boxShadow:'0 2px 10px rgba(0,0,0,.06)', border:'1px solid rgba(0,0,0,.06)',
            position:'relative', overflow:'hidden',
            animation:'mFadeUp .35s ease both',
          }}>
            <div style={{ position:'absolute',top:0,left:0,right:0,height:3,
              background:s.color, borderRadius:'16px 16px 0 0', opacity:.7 }}/>
            <div style={{ fontSize:28, fontWeight:800, color:'#1c1c1e', letterSpacing:-.5, marginBottom:4 }}>
              {s.value}
            </div>
            <div style={{ fontSize:11, color:'#8E8E93', fontWeight:500 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ display:'flex', gap:4, background:'#f0f4f8', borderRadius:12,
        padding:3, marginBottom:20, width:'fit-content', flexWrap:'wrap' }}>
        {['All','Unacknowledged','Pump','Flow Sensor','Higher OutFlow','Refreshments'].map(f => (
          <button key={f} onClick={()=>setFilter(f)} style={{
            padding:'7px 16px', borderRadius:9, border:'none', fontSize:12,
            fontWeight:600, cursor:'pointer', fontFamily:'inherit',
            background: filter===f ? '#fff' : 'transparent',
            color: filter===f ? '#1c1c1e' : '#8E8E93',
            boxShadow: filter===f ? '0 1px 6px rgba(0,0,0,.09)' : 'none',
            transition:'all .18s',
          }}>
            {f}
            {f !== 'All' && (() => {
              const c = f==='Unacknowledged'?unacked:f==='Pump'?pump:f==='Flow Sensor'?sensor
                :f==='Higher OutFlow'?dryrun:f==='Refreshments'?refreshments:0;
              return c > 0 ? <span style={{ marginLeft:5, fontSize:10, fontWeight:800,
                padding:'1px 6px', borderRadius:20,
                background: f==='Unacknowledged'?'rgba(255,59,48,.12)':'rgba(0,0,0,.08)',
                color: f==='Unacknowledged'?'#FF3B30':'#636366',
              }}>{c}</span> : null;
            })()}
          </button>
        ))}
      </div>

      {/* Event list */}
      {loading ? (
        <div style={{ textAlign:'center', padding:'60px 0', color:'#8E8E93' }}>
          <div style={{ width:28,height:28,borderRadius:'50%',border:'3px solid rgba(0,122,255,.12)',
            borderTopColor:'#007AFF',animation:'spin .7s linear infinite',margin:'0 auto 12px' }}/>
          <p style={{ fontSize:13 }}>Loading devices…</p>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 20px', background:'#fff',
          borderRadius:18, border:'1px solid rgba(0,0,0,.06)', color:'#8E8E93' }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d1d6"
            strokeWidth="1.5" strokeLinecap="round" style={{ margin:'0 auto 16px', display:'block' }}>
            <path d="M12 2L2 19h20L12 2Z"/><path d="M12 9v5"/><circle cx="12" cy="17" r="1"/>
          </svg>
          <p style={{ fontWeight:700, color:'#3a3a3c', fontSize:15, marginBottom:6 }}>
            {filter === 'All' ? 'No tamper events recorded' : `No ${filter} events`}
          </p>
          <p style={{ fontSize:13 }}>
            {filter === 'All'
              ? 'Alerts will appear here when tamper is detected on any device'
              : 'Try changing the filter above'}
          </p>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {filtered.map((ev, i) => {
            const cfg   = TAM_CFG[ev.tamCode] || TAM_CFG['1'];
            const acked = !!ev.acknowledgedAt;
            return (
              <div key={ev.id} style={{
                background:'#fff', borderRadius:14, padding:'16px 18px',
                border:`1px solid ${acked?'rgba(0,0,0,.06)':cfg.color+'28'}`,
                boxShadow: acked?'0 1px 4px rgba(0,0,0,.05)':'0 2px 12px rgba(0,0,0,.07)',
                display:'flex', gap:14, alignItems:'flex-start',
                opacity: acked ? .65 : 1,
                animation:`mFadeUp .3s ease ${i*.04}s both`,
              }}>
                {/* Icon */}
                <div style={{
                  width:40, height:40, borderRadius:12, flexShrink:0,
                  background: acked ? '#f0f0f0' : `linear-gradient(135deg,${cfg.color},${cfg.color}cc)`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  boxShadow: acked ? 'none' : `0 3px 10px ${cfg.color}40`,
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2L2 19h20L12 2Z" fill={acked?'#8E8E93':'white'} fillOpacity=".3"
                      stroke={acked?'#8E8E93':'white'} strokeWidth="1.5" strokeLinejoin="round"/>
                    <path d="M12 9v5" stroke={acked?'#8E8E93':'white'} strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="12" cy="17" r="1" fill={acked?'#8E8E93':'white'}/>
                  </svg>
                </div>

                {/* Info */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:5, flexWrap:'wrap' }}>
                    <span style={{ fontSize:14, fontWeight:700, color:'#1c1c1e' }}>{ev.title}</span>
                    <span style={{ fontSize:9, fontWeight:800, padding:'2px 8px', borderRadius:20,
                      background:`${cfg.color}15`, color:cfg.color, letterSpacing:.6 }}>{cfg.badge}</span>
                    {acked && (
                      <span style={{ fontSize:10, fontWeight:600, color:'#34C759',
                        background:'rgba(52,199,89,.08)', padding:'2px 8px', borderRadius:20 }}>
                        Acknowledged
                      </span>
                    )}
                    {!acked && (
                      <span style={{ fontSize:10, fontWeight:700, color:'#FF3B30',
                        background:'rgba(255,59,48,.08)', padding:'2px 8px', borderRadius:20 }}>
                        Pending
                      </span>
                    )}
                  </div>

                  <div style={{ fontSize:13, color:'#48484a', marginBottom:6 }}>
                    <strong>Device:</strong> {ev.deviceName}
                    <span style={{ fontFamily:'monospace', fontSize:11, color:'#8E8E93', marginLeft:8 }}>
                      {ev.mac}
                    </span>
                  </div>
                  {(ev.location || ev.serialNo) && (
                    <div style={{ display:'flex', gap:12, marginBottom:6, flexWrap:'wrap' }}>
                      {ev.location && (
                        <span style={{ fontSize:11, color:'#48484a', display:'flex', alignItems:'center', gap:3 }}>
                          📍 {ev.location}
                        </span>
                      )}
                      {ev.serialNo && (
                        <span style={{ fontSize:11, color:'#48484a', display:'flex', alignItems:'center', gap:3 }}>
                          🔖 {ev.serialNo}
                        </span>
                      )}
                    </div>
                  )}

                  <div style={{ display:'flex', gap:16, flexWrap:'wrap', alignItems:'center' }}>
                    <span style={{ fontSize:11, color:'#8E8E93', display:'flex', alignItems:'center', gap:4 }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                      </svg>
                      {new Date(ev.detectedAt).toLocaleString('en-IN',{
                        day:'2-digit', month:'short', year:'numeric',
                        hour:'2-digit', minute:'2-digit', hour12:true,
                      })}
                    </span>
                    {ev.acknowledgedAt && (
                      <span style={{ fontSize:11, color:'#34C759', display:'flex', alignItems:'center', gap:3 }}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                          <path d="M20 6L9 17l-5-5"/>
                        </svg>
                        Ack: {new Date(ev.acknowledgedAt).toLocaleString('en-IN',{
                          day:'2-digit', month:'short',
                          hour:'2-digit', minute:'2-digit', hour12:true,
                        })}
                      </span>
                    )}
                  </div>
                </div>

                {/* Acknowledge button */}
                {!acked && (
                  <button onClick={() => handleAck(ev.mac, ev.id)} style={{
                    padding:'8px 14px', borderRadius:10, border:'none', flexShrink:0,
                    background:`linear-gradient(135deg,${cfg.color},${cfg.color}cc)`,
                    color:'#fff', fontWeight:700, fontSize:12,
                    cursor:'pointer', fontFamily:'inherit',
                    boxShadow:`0 2px 8px ${cfg.color}40`,
                  }}>
                    Support
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
